/**
 * Sprint 87 — AutoSenseBuild
 *
 * Reads the uploaded DK entry template, shows what we detected per contest
 * (real field size + max entries, and where that number came from), the
 * tuning each group will be built with, then builds one portfolio per tuning
 * and binds every lineup to the contest it was tuned for.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, DownloadSimple, Receipt, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { loadOverrides } from "@/lib/overridesStore";
import { downloadDkCsv } from "@/lib/dkDownload";

const SRC_CHIP = {
  dk_lobby: { label: "live DK lobby", cls: "border-[#39FF14]/60 text-[#39FF14]" },
  contest_name: { label: "contest name", cls: "border-[#FFCC00]/60 text-[#FFCC00]" },
  heuristic: { label: "estimate", cls: "border-zinc-700 text-zinc-400" },
};

export default function AutoSenseBuild({ source = "smart" }) {
  const [plan, setPlan] = useState(null);
  const [planErr, setPlanErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [build, setBuild] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [starting, setStarting] = useState(false);
  const poll = useRef(null);

  const loadPlan = async () => {
    setLoading(true);
    setPlanErr(null);
    try {
      setPlan(await api.entryTemplateAutoPlan());
    } catch (e) {
      setPlanErr(e?.response?.data?.detail || "Could not read the template");
    } finally {
      setLoading(false);
    }
  };

  const loadReceipt = async () => {
    try {
      const r = await api.entryTemplateReceipt();
      if (r?.receipt) setReceipt(r.receipt);
    } catch { /* no receipt yet */ }
  };

  useEffect(() => {
    loadPlan();
    loadReceipt();
    api.entryTemplateAutoStatus().then((s) => setBuild(s?.build || null)).catch(() => {});
    return () => clearInterval(poll.current);
  }, []);

  const startPolling = () => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const s = await api.entryTemplateAutoStatus();
        setBuild(s?.build || null);
        if (s?.build?.status === "done") {
          clearInterval(poll.current);
          await loadReceipt();
          window.dispatchEvent(new CustomEvent("wp:auto-build-done"));
          toast.success(`Auto-build complete · ${s.build.lineups_built} lineups across your contests`);
        } else if (s?.build?.status === "partial") {
          clearInterval(poll.current);
          await loadReceipt();
          window.dispatchEvent(new CustomEvent("wp:auto-build-done"));
          toast.warning(`Built ${s.build.lineups_built}/${s.build.lineups_total} lineups · ${s.build.blank_entries} DK entries will be blank`, {
            description: "See the build receipt for which contests fell short and why.",
          });
        } else if (s?.build?.status === "error") {
          clearInterval(poll.current);
          toast.error(s.build.error || "Auto-build failed");
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  const onBuild = async (opts = {}) => {
    setStarting(true);
    try {
      const overrides = loadOverrides();
      const r = await api.entryTemplateAutoBuild({ player_overrides: overrides });
      toast.success(`Auto-build started · ${r.groups} tunings · ${r.total_lineups} lineups`);
      setBuild({ status: "running", groups_total: r.groups, groups_done: 0, lineups_built: 0, lineups_total: r.total_lineups });
      startPolling();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      const msg = typeof d === "string" ? d : d?.message || "Auto-build failed to start";
      // Sprint 103 — say WHAT is blocking Auto-Sense and offer the one tap
      // that clears it, instead of the old "a build is already running".
      if (e?.response?.status === 409 && d?.can_stop && !opts.retried) {
        const go = window.confirm(`${msg}\n\nStop it and start Auto-Sense now?`);
        if (go) {
          try {
            if (code === "AUTO_BUILD_RUNNING") await api.entryTemplateAutoReset();
            else await api.optimizeStop(true);
            await new Promise((r2) => setTimeout(r2, 1200));
            setStarting(false);
            return onBuild({ retried: true });
          } catch {
            toast.error("Could not stop the running build — try Reset build");
          }
        } else {
          toast.error(msg);
        }
      } else {
        toast.error(msg);
      }
    } finally {
      setStarting(false);
    }
  };

  const onReset = async () => {
    try {
      await api.entryTemplateAutoReset();
      clearInterval(poll.current);
      setBuild(null);
      toast.success("Build state cleared — you can start a new auto-build");
    } catch {
      toast.error("Could not clear the build state");
    }
  };

  const onDownload = async () => {
    try {
      await downloadDkCsv(api.entryTemplateExportUrl(), "warpaint_dk_multi_contest.csv");
      toast.success("Downloaded · upload this file back to DraftKings");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  const running = build?.status === "running";
  const groups = plan?.groups || [];
  const contests = plan?.contests || [];

  return (
    <div className="border border-[#FFCC00]/30 bg-black/40 p-3 mt-3" data-testid={`auto-sense-${source}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold flex items-center gap-1">
          <Sparkle size={12} weight="fill" /> Auto-sense · one tuning per tournament
        </div>
        <button type="button" onClick={loadPlan} disabled={loading}
          data-testid={`auto-sense-refresh-${source}`}
          className="mono text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-[#FFCC00] disabled:opacity-40 flex items-center gap-1">
          <ArrowClockwise size={10} />{loading ? "Reading…" : "Re-read template"}
        </button>
      </div>

      {planErr && (
        <div className="mono text-[10px] text-[#FF8A00]" data-testid={`auto-sense-error-${source}`}>
          {String(planErr)}
        </div>
      )}

      {plan && (
        <>
          <div className="mono text-[10px] text-zinc-400 mb-2" data-testid={`auto-sense-summary-${source}`}>
            {contests.length} contest{contests.length === 1 ? "" : "s"} · {plan.total_entries} reserved entr{plan.total_entries === 1 ? "y" : "ies"} ·{" "}
            {groups.length} distinct tuning{groups.length === 1 ? "" : "s"} ·{" "}
            <span className={plan.lobby_hits ? "text-[#39FF14]" : "text-[#FFCC00]"}>
              {plan.lobby_hits}/{contests.length} read live from DK
            </span>
          </div>

          <div className="border border-zinc-900 bg-black max-h-44 overflow-y-auto mb-2">
            <table className="w-full text-[11px] mono">
              <thead className="bg-[#0e0e0e] text-zinc-500 sticky top-0">
                <tr className="text-left uppercase tracking-widest text-[9px]">
                  <th className="px-2 py-1.5">Contest</th>
                  <th className="px-2 py-1.5 text-right">Entries</th>
                  <th className="px-2 py-1.5 text-right">Max/user</th>
                  <th className="px-2 py-1.5 text-right">Field</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Read from</th>
                </tr>
              </thead>
              <tbody>
                {contests.map((c) => {
                  const chip = SRC_CHIP[c.source] || SRC_CHIP.heuristic;
                  return (
                    <tr key={c.contest_id} className="border-t border-zinc-900"
                        data-testid={`auto-sense-row-${c.contest_id}`}>
                      <td className="px-2 py-1 text-zinc-300 truncate max-w-[220px]">{c.contest_name}</td>
                      <td className="px-2 py-1 text-right text-white">{c.entries_reserved}</td>
                      <td className="px-2 py-1 text-right text-white">{c.max_entries}</td>
                      <td className="px-2 py-1 text-right text-zinc-300">{(c.field_size || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-zinc-400">{c.contest_type}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1 py-[1px] border text-[9px] ${chip.cls}`}>{chip.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-1.5 mb-2">
            {groups.map((g) => (
              <div key={g.group_key} className="border border-zinc-800 bg-[#0b0b0b] px-2.5 py-1.5"
                   data-testid={`auto-sense-group-${g.group_key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="mono text-[10px] text-white font-bold">{g.label}</span>
                  <span className="mono text-[9px] text-zinc-500">
                    {g.num_lineups} lineup{g.num_lineups === 1 ? "" : "s"} · {g.contests.length} contest{g.contests.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-400 leading-snug mt-0.5">{g.explain}</div>
              </div>
            ))}
          </div>

          {running ? (
            <div className="border border-[#FFCC00]/40 bg-[#1a1405] px-3 py-2" data-testid={`auto-sense-progress-${source}`}>
              <div className="mono text-[10px] text-[#FFCC00]">
                Building {build.groups_done + 1}/{build.groups_total} · {build.current_label || "…"}
              </div>
              <div className="mono text-[9px] text-zinc-500 mt-0.5">
                {build.lineups_built}/{build.lineups_total} lineups placed
              </div>
              <div className="h-1 bg-zinc-900 mt-1.5">
                <div className="h-1 bg-[#FFCC00] transition-all"
                     style={{ width: `${Math.round(100 * (build.lineups_built / Math.max(1, build.lineups_total)))}%` }} />
              </div>
              <button type="button" onClick={onReset} data-testid={`auto-sense-reset-${source}`}
                className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] mt-1.5">
                Stuck? Reset build state
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => onBuild()} disabled={starting || !groups.length}
              data-testid={`auto-sense-build-${source}`}
              className="w-full py-2.5 bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] transition-colors">
              {starting ? "Starting…" : `Auto-build ${plan.total_entries} entries · ${groups.length} tunings`}
            </button>
          )}
        </>
      )}

      {receipt && (
        <div className="mt-3 border border-zinc-800 bg-black/40 p-2.5" data-testid={`auto-sense-receipt-${source}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold flex items-center gap-1 mb-1.5">
            <Receipt size={12} weight="fill" /> Build receipt · how your entries were filled
          </div>
          <div className="mono text-[9px] text-zinc-500 mb-2">
            {receipt.total_lineups} lineups · {receipt.contests.length} contests · {receipt.lobby_hits} read live from DK · built {new Date(receipt.built_at).toLocaleString()}
          </div>
          {!!receipt.blank_entries && (
            <div className="border border-[#FF8A00]/60 bg-[#1a1005] px-2.5 py-2 mb-2" data-testid={`auto-sense-shortfall-${source}`}>
              <div className="mono text-[10px] text-[#FF8A00] font-bold">
                {receipt.blank_entries} DK entr{receipt.blank_entries === 1 ? "y" : "ies"} will upload blank
              </div>
              {(receipt.warnings || []).map((w, i) => (
                <div key={i} className="text-[10px] text-zinc-400 leading-snug mt-0.5">{w}</div>
              ))}
              <div className="text-[10px] text-zinc-500 leading-snug mt-1">
                DK ignores blank rows, so the rest still enters. Loosen your locks/excludes or lower the lineup
                count for those contests and auto-build again to fill them.
              </div>
            </div>
          )}
          {!receipt.blank_entries && !!(receipt.warnings || []).length && (
            <div className="border border-zinc-800 bg-black/30 px-2.5 py-1.5 mb-2" data-testid={`auto-sense-adapted-${source}`}>
              <div className="mono text-[9px] uppercase tracking-widest text-[#FFCC00]">How we adapted</div>
              {receipt.warnings.map((w, i) => (
                <div key={i} className="text-[10px] text-zinc-400 leading-snug mt-0.5">{w}</div>
              ))}
            </div>
          )}
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {receipt.contests.map((c) => (
              <div key={c.contest_id} className="border-b border-zinc-900 pb-1.5"
                   data-testid={`auto-sense-receipt-row-${c.contest_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[11px] text-zinc-200 truncate max-w-[260px]">{c.contest_name}</span>
                  <span className={`mono text-[9px] ${c.shortfall ? "text-[#FF8A00]" : "text-[#39FF14]"}`}>
                    {c.lineups_assigned}/{c.entries_reserved} slots filled
                    {c.shortfall ? ` · ${c.shortfall} blank` : ""}
                  </span>
                </div>
                <div className="mono text-[9px] text-zinc-500">
                  detected {Number(c.detected_field_size).toLocaleString()}-entry field · {c.detected_max_entries}-max ({c.detection_source_label})
                  {" · avg proj "}{c.avg_projection}{" · avg own "}{c.avg_ownership}%
                </div>
                <div className="text-[10px] text-zinc-400 leading-snug">{c.explain}</div>
              </div>
            ))}
          </div>
          <button type="button" onClick={onDownload} data-testid={`auto-sense-download-${source}`}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-[11px] transition-colors">
            <DownloadSimple size={14} weight="bold" /> Download multi-contest CSV
          </button>
        </div>
      )}
    </div>
  );
}
